import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { sha256 } from "js-sha256";
import UserAvatar from "./UserAvatar";
import { SidebarMenuButton, useSidebar } from "../sidebar";
import {
  ArrowDownUp,
  Plug,
  ChevronsUpDown,
  LogOut,
  Settings,
  Shield,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../dropdown-menu";
import { logout } from "@/common/auth";
import { useIsAdmin } from "@/api/hooks";
import UserSettingsSheet from "./UserSettingsSheet";
import SpaceTransferSheet from "./SpaceTransferSheet";
import ConnectionsSheet from "./ConnectionsSheet";

export default function UserMenu() {
  const me = useAppSelector(selectMe);
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const [gravatarUrl, setGravatarUrl] = useState<string | null>(null);
  const { isMobile } = useSidebar();
  // Controlled from here rather than wrapping the items in a SheetTrigger: the
  // dropdown unmounts its items on select, which would close the sheet with it.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [connectionsOpen, setConnectionsOpen] = useState(false);

  const email = me?.email ?? null;
  const picture = me?.picture ?? null;
  // Only worth asking when the account has neither an uploaded avatar nor a
  // provider picture — a gravatar is the last stop before initials.
  const shouldTryGravatar = !!email && !picture;

  // Discard a stale gravatar the moment it's no longer applicable, rather
  // than waiting a render for the effect below to catch up.
  const [gravatarUrlForShould, setGravatarUrlForShould] =
    useState(shouldTryGravatar);
  if (shouldTryGravatar !== gravatarUrlForShould) {
    setGravatarUrlForShould(shouldTryGravatar);
    if (!shouldTryGravatar) setGravatarUrl(null);
  }

  useEffect(() => {
    if (!shouldTryGravatar || !email) return;

    const potentialGravatarURL =
      "https://0.gravatar.com/avatar/" + sha256(email) + "?d=404";

    let cancelled = false;
    fetch(potentialGravatarURL).then((value: Response) => {
      if (!cancelled && value.ok) {
        setGravatarUrl(potentialGravatarURL);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [shouldTryGravatar, email]);

  if (!me) {
    return null;
  }

  const displayName = me.name || me.nickname.nickname || me.id;

  return (
    <>
      <UserSettingsSheet open={settingsOpen} onOpenChange={setSettingsOpen} />
      <SpaceTransferSheet open={transferOpen} onOpenChange={setTransferOpen} />
      <ConnectionsSheet
        open={connectionsOpen}
        onOpenChange={setConnectionsOpen}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            size="lg"
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <UserAvatar
              name={displayName}
              picture={picture || gravatarUrl}
              className="h-8 w-8 rounded-lg"
              fallbackClassName="rounded-lg text-xs font-medium"
            />
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{displayName}</span>
              {email && <span className="truncate text-xs">{email}</span>}
            </div>
            <ChevronsUpDown className="ml-auto size-4" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
          side={isMobile ? "bottom" : "right"}
          align="end"
          sideOffset={4}
        >
          {isAdmin && (
            <DropdownMenuItem onClick={() => navigate("/admin")}>
              <Shield />
              Users
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setSettingsOpen(true)}>
            <Settings />
            Account settings
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setTransferOpen(true)}>
            <ArrowDownUp />
            Import and export
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setConnectionsOpen(true)}>
            <Plug />
            Connected accounts
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout}>
            <LogOut />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
